/**
 * The routes the payment provider returns the customer to.
 *
 * These are the frontend halves of the backend's Payment:AcceptUrl and Payment:CancelUrl
 * configuration. If the two ever disagree, a customer lands on a 404 immediately after
 * paying -- so they live here, in one place, rather than being spelled out at each use.
 *
 * Both routes must work when opened cold: the customer has been away on the provider's
 * domain and may have refreshed or lost all in-memory state.
 */

import { getPendingOrderId } from "__utilities/functions/sessionHandler";

/** Payment:AcceptUrl -> {origin}/payment/{orderId}/success */
export const PAYMENT_SUCCESS_ROUTE = "/payment/:id/success";

/** Payment:CancelUrl -> {origin}/payment/{orderId}/cancel */
export const PAYMENT_CANCEL_ROUTE = "/payment/:id/cancel";

/**
 * Order ids are generated server-side as 20 alphanumeric characters, in a column that allows 25.
 * Deliberately a shape test rather than an exact length: it has to reject something that is
 * plainly not an order id without breaking if the generator's length is ever changed.
 */
const ORDER_ID_PATTERN = /^[a-z0-9]{10,25}$/i;

/**
 * Resolves the order the customer has just come back from paying for.
 *
 * The route segment is only trusted once it looks like an order id. The backend's
 * `Payment:AcceptUrl` / `Payment:CancelUrl` are templates containing `{0}`, so a configuration
 * that does not substitute the order id lands the customer here with a literal `{0}` in the
 * path -- which is truthy, and so was taken in preference to the id we stored ourselves before
 * redirecting. Every call made with it then failed for the rest of the visit.
 *
 * Falls back to what was persisted before leaving, which is also what makes these pages work
 * when opened cold.
 */
export const resolveOrderId = (routeId) => {
  if (typeof routeId === "string" && ORDER_ID_PATTERN.test(routeId) === true) {
    return routeId;
  }

  return getPendingOrderId();
};
